import { useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  Typography,
  getTypographyDefaultColor,
  Button,
  Space,
  Col,
  Row,
  Container,
  Modal
} from '@ag1/ui';
import {
  BenefitsV2 as BenefitsProps,
  SanityKeyed,
  UnifiedButton as UnifiedButtonType
} from 'types/studio/studio';
import { getThemeCSSClass } from 'shared/utils/getThemeCSSClass';
import { getSanityLinkUrl } from 'shared/utils/getSanityLinkUrl';
import { useLocale } from 'shared/hooks/useLocale';
import { getSectionPaddingClasses } from 'shared/utils/getSectionPaddingClasses';
import IngredientModal from '@portal/shared/modals/IngredientModal/IngredientModal';
import { StudioBaseImageV2 } from '../StudioBaseImage/StudioBaseImage';
import { StudioBaseIconV2 } from '../StudioBaseIcon/StudioBaseIcon';
import CustomPortableText from '../PortableText/PortableText';
import styles from './BenefitsV2.module.scss';
import { CommonSectionProps } from '../types';
import { UnifiedButton } from '../UnifiedButton';

const BenefitsV2 = ({
  _type,
  variant,
  _key: key,
  headline,
  body,
  image,
  removeImageOnMobile,
  points,
  switchButton,
  modal,
  anchor,
  anchorButtonLink,
  link,
  btnTextMobile,
  badgesOptions,
  theme,
  padding,
  unifiedButton,
  visibleOnPageLoad
}: SanityKeyed<BenefitsProps> & CommonSectionProps) => {
  const colSpanConfig = variant !== 'image_false' ? { xxs: 12, m: 6 } : { xxs: 12, l: 8 };
  const [isOpen, setOpen] = useState(false);

  const locale = useLocale();

  const buttonLink = useMemo(
    () => getSanityLinkUrl(link?.btnLink, locale),
    [locale, link?.btnLink]
  );

  // Determine if UnifiedButton should be used
  const shouldUseUnifiedButton = useMemo(
    () => !!unifiedButton?.switchButton && unifiedButton.switchButton !== 'noButton',
    [unifiedButton]
  );

  return (
    <>
      <Container narrow id={key}>
        <Space
          top={{ xxs: 40, s: 60, m: 80, l: 100 }}
          bottom={{ xxs: 40, s: 60, m: 80, l: 100 }}
          className={clsx(
            getSectionPaddingClasses(styles, padding),
            image?.image?.asset && !removeImageOnMobile && styles.removeMobileTopPadding,
            variant === 'image_false' && styles.spaceNoImage
          )}>
          <Row
            align={{ m: 'between' }}
            className={clsx(styles.row, variant === 'image_left' && styles.rowReverse)}>
            <Col span={colSpanConfig} className={styles.col}>
              {headline && variant !== 'image_false' && (
                <Typography
                  variant="headlineSans"
                  size="2"
                  component="h2"
                  color={getTypographyDefaultColor(theme)}
                  className={clsx(removeImageOnMobile && styles.noPadding, styles.title)}
                  noPadding>
                  {headline}
                </Typography>
              )}
              {body && <CustomPortableText value={body} />}
              <Row
                className={clsx(
                  styles.itemRow,
                  variant === 'image_false' && styles.itemRowNoImage
                )}>
                {points.map(({ icon, iconBg, sellingpoint, description, _key }) => (
                  <Col
                    span={{ xxs: 12, s: 6 }}
                    key={_key}
                    className={clsx(styles.item, variant === 'image_false' && styles.itemWide)}>
                    {icon?.asset && (
                      <div
                        className={clsx(
                          styles.itemIcon,
                          iconBg === 'white' && styles.white,
                          variant === 'image_false' && styles.itemIconWide,
                          getThemeCSSClass(theme)
                        )}>
                        <StudioBaseIconV2
                          icon={icon}
                          className={clsx(
                            styles.icon,
                            variant === 'image_false' && styles.iconWide,
                            getThemeCSSClass(theme)
                          )}
                        />
                      </div>
                    )}
                    <div
                      className={clsx(
                        variant === 'image_false' && styles.itemInfoWide,
                        variant === 'image_false' && !icon && styles.itemInfoWideNoIcon
                      )}>
                      <Typography
                        variant="subtitle"
                        size="2"
                        weight="700"
                        noPadding
                        className={clsx(
                          styles.itemSubtitle,
                          variant === 'image_false' && styles.itemSubtitleWide,
                          getThemeCSSClass(theme)
                        )}>
                        {sellingpoint}
                      </Typography>
                      {description && (
                        <CustomPortableText
                          value={description}
                          addAccessibility
                          className={clsx(
                            styles.itemDescription,
                            variant === 'image_false' && styles.itemDescriptionWide,
                            getThemeCSSClass(theme)
                          )}
                        />
                      )}
                    </div>
                  </Col>
                ))}
                {/* --- Conditional Button Rendering --- */}
                {shouldUseUnifiedButton ? (
                  <Col span={{ xxs: 12 }} className={styles.buttonWrap}>
                    <UnifiedButton
                      unifiedButton={unifiedButton as UnifiedButtonType}
                      sectionType={_type}
                      size={{ xxs: 'medium', xl: 'large' }}
                      theme={theme}
                      className={styles.button}
                    />
                  </Col>
                ) : (
                  <>
                    {/* --- Fallback Button Rendering --- */}
                    {variant !== 'image_false' && switchButton === 'modal' && modal && (
                      <Col span={{ xxs: 12 }} className={styles.buttonWrap}>
                        <Button
                          type="button"
                          variant={modal.variant}
                          size={{ xxs: 'medium', xl: 'large' }}
                          rounded
                          fluid
                          icon={modal.showIcon && modal.iconOptions?.icon}
                          iconPosition={modal.iconOptions?.position}
                          className={styles.button}
                          onClick={() => setOpen(true)}
                          theme={theme}>
                          {modal.portableText && (
                            <CustomPortableText
                              value={modal.portableText}
                              className={btnTextMobile ? styles.hideOnMobile : ''}
                            />
                          )}
                          {btnTextMobile && (
                            <CustomPortableText
                              value={btnTextMobile}
                              className={styles.btnTextMobile}
                            />
                          )}
                        </Button>
                      </Col>
                    )}
                    {variant !== 'image_false' && switchButton === 'link' && link && (
                      <Col span={{ xxs: 12 }}>
                        <div className={styles.buttonWrap}>
                          <Button
                            type="button"
                            variant={link.variant}
                            href={buttonLink}
                            target={link?.targetBlank ? '_blank' : '_self'}
                            size={{ xxs: 'medium', xl: 'large' }}
                            rounded
                            fluid
                            icon={link.showIcon && link.iconOptions?.icon}
                            iconPosition={link.iconOptions?.position}
                            className={styles.button}
                            theme={theme}>
                            {link.portableText && (
                              <CustomPortableText
                                value={link.portableText}
                                className={btnTextMobile ? styles.hideOnMobile : ''}
                              />
                            )}
                            {btnTextMobile && (
                              <CustomPortableText
                                value={btnTextMobile}
                                className={styles.btnTextMobile}
                              />
                            )}
                          </Button>
                        </div>
                      </Col>
                    )}
                    {variant !== 'image_false' && switchButton === 'anchor' && anchor && (
                      <Col span={{ xxs: 12 }}>
                        <div className={styles.buttonWrap}>
                          <Button
                            size="medium"
                            rounded
                            fluid
                            variant={anchor?.variant}
                            href={`#${anchorButtonLink}`}
                            icon={anchor?.showIcon && anchor?.iconOptions?.icon}
                            iconPosition={anchor?.iconOptions?.position}
                            className={styles.button}>
                            {anchor?.portableText && (
                              <CustomPortableText
                                value={anchor?.portableText}
                                className={btnTextMobile ? styles.hideOnMobile : ''}
                              />
                            )}
                            {btnTextMobile && (
                              <CustomPortableText
                                value={btnTextMobile}
                                className={styles.btnTextMobile}
                              />
                            )}
                          </Button>
                        </div>
                      </Col>
                    )}
                    {/* --- End Fallback Buttons --- */}
                  </>
                )}
                {/* Conditionally render badges only if not using Unified Button and variant allows */}
                {!shouldUseUnifiedButton && variant === 'image_false' && badgesOptions?.badges && (
                  <Col span={{ xxs: 12 }}>
                    <div className={styles.badgesWrap}>
                      {badgesOptions?.badgesSet?.map((item) => (
                        <StudioBaseIconV2 key={item._key} icon={item} className={styles.badge} />
                      ))}
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
            {variant !== 'image_false' && image?.image && (
              <Col span={{ xxs: 12, m: 6, l: 5 }}>
                <StudioBaseImageV2
                  image={image.image}
                  tabletImage={image.tabletImage}
                  mobileImage={image.mobileImage}
                  sizes={{ xxs: '100vw', m: '50vw', l: '600px' }}
                  className={clsx(
                    styles.imageCont,
                    variant === 'image_left' && styles.imageContReverse,
                    removeImageOnMobile ? styles.displayNone : ''
                  )}
                  childClassName={styles.image}
                  eagerLoading={visibleOnPageLoad}
                />
              </Col>
            )}
          </Row>
        </Space>
      </Container>
      {/* --- Fallback Modal --- */}
      {!shouldUseUnifiedButton && switchButton === 'modal' && modal && (
        <Modal isOpen={isOpen} onClose={() => setOpen(false)} mode="fullScreen">
          <div className={styles.modalInner}>
            <IngredientModal isOpen={isOpen} onClose={() => setOpen(false)} theme={theme} />
          </div>
        </Modal>
      )}
    </>
  );
};
export default BenefitsV2;
